import { PlanTrailActionContext, getMap, getLayerRegistry, safeRemoveLayer } from "./types";
import { MarkerFeature } from "../../../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { plainToClass } from "class-transformer";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import mapboxgl from "mapbox-gl";
import type { FeatureCollection, Feature, Point } from 'geojson';
import { ServiceProvider } from "../../../services/ServiceProvider";
import {sanitizeCoords} from "../../../../../utils/sanitizeCoords";
import { RemoveMarkerCommand } from "../../undo/commands/RemoveMarkerCommand";
import { MoveMarkerCommand } from "../../undo/commands/MoveMarkerCommand";
import { RemoveAllControlPointsCommand } from "../../undo/commands/RemoveAllControlPointsCommand";

// Store function references for cleanup
let functionRefs: { [key: string]: Function[] } = {};

// Consolidated marker layer/source identifiers and in-memory data store
const CONSOLIDATED_MARKER_SOURCE = 'consolidated-routes-markers';
const CONSOLIDATED_MARKER_LAYER = 'consolidated-routes-marker-layer';
let consolidatedMarkersData: FeatureCollection | null = null;
let markerIndexById: Record<string, number> = {};

// Clean up all marker event listeners (called before re-rendering routes)
export function cleanupAllMarkerListeners() {
  const map = getMap();
  if (!map) return;

  console.log('[MARKERS] cleanupAllMarkerListeners called, cleaning up', Object.keys(functionRefs).length, 'layers');
  // Remove all stored event listeners
  Object.keys(functionRefs).forEach((layerId) => {
    if (functionRefs[layerId]) {
      functionRefs[layerId].forEach((ref) => {
        map.off("mouseenter", layerId, ref);
        map.off("mouseleave", layerId, ref);
        map.off("mousedown", layerId, ref);
        map.off("touchstart", layerId, ref);
      });
    }
  });

  // Clear the references
  functionRefs = {};
  console.log('[MARKERS] cleanupAllMarkerListeners complete');
}

export const markerActions = {
  /**
   * Build a consolidated FeatureCollection for the active route's markers
   */
  buildConsolidatedMarkersData({ state }: PlanTrailActionContext): FeatureCollection | null {
    const activeRoute = state.activeRouteKey ? state.routes[state.activeRouteKey] : null;
    if (!activeRoute || activeRoute.markers.length === 0) return null;

    const features: Feature<Point>[] = [] as any;
    markerIndexById = {};

    for (let i = 0; i < activeRoute.markers.length; i++) {
      const m = activeRoute.markers[i];
      const coords = m.markerGeometry.coordinates as [number, number];
      const isFirst = i === 0;
      const isLast = i === activeRoute.markers.length - 1;
      const markerId = `route-${activeRoute.id}-marker-${i}`;
      markerIndexById[markerId] = i;

      const feature: Feature<Point> = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [coords[0], coords[1]]
        },
        properties: {
          routeId: activeRoute.id,
          routeKey: state.activeRouteKey,
          markerIndex: i,
          markerId,
          position: isFirst ? 'start' : (isLast ? 'end' : 'mid'),
          isActive: true
        }
      } as any;

      features.push(feature);
    }

    return {
      type: 'FeatureCollection',
      features
    };
  },

  /**
   * Add a single consolidated layer/source for markers with data-driven styling
   */
  async addConsolidatedMarkersToMap({ state, commit, dispatch }: PlanTrailActionContext) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();

    // Build dataset from active route only (disabled routes have no markers)
    consolidatedMarkersData = (markerActions as any).buildConsolidatedMarkersData({ state } as any);
    console.log('[MARKERS] addConsolidatedMarkersToMap called, has data:', !!consolidatedMarkersData);
    if (!consolidatedMarkersData) {
      console.log('[MARKERS] No markers data, cleaning up listeners and removing layer');
      // Clean up event listeners before removing layer
      cleanupAllMarkerListeners();

      // If nothing to show, remove existing layer/source once
      safeRemoveLayer(CONSOLIDATED_MARKER_LAYER);
      if (map.getSource(CONSOLIDATED_MARKER_SOURCE)) {
        try { map.removeSource(CONSOLIDATED_MARKER_SOURCE); } catch (_) {}
      }
      return;
    }

    // If already present, just update data to avoid flashing
    const existingSource = map.getSource(CONSOLIDATED_MARKER_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    const existingLayer = map.getLayer(CONSOLIDATED_MARKER_LAYER);
    console.log('[MARKERS] Existing source:', !!existingSource, 'Existing layer:', !!existingLayer);
    console.log('[MARKERS] Has listeners:', !!functionRefs[CONSOLIDATED_MARKER_LAYER], 'Count:', functionRefs[CONSOLIDATED_MARKER_LAYER]?.length || 0);
    if (existingSource && existingLayer) {
      try { existingSource.setData(consolidatedMarkersData as any); } catch (_) {}
      // Check if listeners are already attached
      if (functionRefs[CONSOLIDATED_MARKER_LAYER] && functionRefs[CONSOLIDATED_MARKER_LAYER].length > 0) {
        console.log('[MARKERS] Listeners already attached, returning');
        return;
      }
      console.log('[MARKERS] Listeners missing, will re-attach');
      // If listeners are missing, fall through to re-attach them below (but don't re-add source/layer)
    } else {
      // Source or layer doesn't exist, so create them
      // Add source
      layerRegistry.addSource(CONSOLIDATED_MARKER_SOURCE, {
        type: 'geojson',
        data: consolidatedMarkersData
      }, true);

      // Data-driven icon selection based on position
      const iconImageExpr: any = [
        'case',
        ['==', ['get', 'position'], 'start'], 'hike-control-point-start',
        ['==', ['get', 'position'], 'end'], 'hike-control-point-end',
        'hike-control-point-mid'
      ];

      const layer: mapboxgl.SymbolLayer = {
        id: CONSOLIDATED_MARKER_LAYER,
        type: 'symbol',
        source: CONSOLIDATED_MARKER_SOURCE,
        layout: {
          'icon-image': iconImageExpr,
          'icon-size': 0.25,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'center'
        },
        paint: {
          'icon-opacity': 1.0
        }
      };

      layerRegistry.addLayer(layer, MapLayerCategory.Annotations, { z: 13 });
    }

    // Ensure layer is actually added before attaching listeners
    if (!map.getLayer(CONSOLIDATED_MARKER_LAYER)) {
      console.error('[MARKERS] Layer was not added to map, cannot attach listeners');
      return;
    }

    // Clean up any existing listeners before attaching new ones
    console.log('[MARKERS] Cleaning up existing listeners before attaching new ones');
    if (functionRefs[CONSOLIDATED_MARKER_LAYER]) {
      functionRefs[CONSOLIDATED_MARKER_LAYER].forEach((ref) => {
        map.off("mouseenter", CONSOLIDATED_MARKER_LAYER, ref);
        map.off("mouseleave", CONSOLIDATED_MARKER_LAYER, ref);
        map.off("mousedown", CONSOLIDATED_MARKER_LAYER, ref);
        map.off("touchstart", CONSOLIDATED_MARKER_LAYER, ref);
      });
    }

    // Attach consolidated drag listeners
    console.log('[MARKERS] Attaching new listeners');
    const canvas = map.getCanvasContainer();

    let startClickTime: number | null = null;
    let lastDragLngLat: mapboxgl.LngLatLike | null = null;
    let currentMarkerId: string | null = null;
    let currentMarkerIndex: number | null = null;

    async function onMove(e: mapboxgl.MapMouseEvent) {
      console.log('[MARKERS] onMove called, currentMarkerIndex:', currentMarkerIndex);
      const src = map.getSource(CONSOLIDATED_MARKER_SOURCE) as mapboxgl.GeoJSONSource;
      if (!src || !consolidatedMarkersData || currentMarkerIndex == null) {
        console.log('[MARKERS] onMove early return - src:', !!src, 'data:', !!consolidatedMarkersData, 'index:', currentMarkerIndex);
        return;
      }
      const coords = e.lngLat;
      lastDragLngLat = coords;
      console.log('[MARKERS] onMove dragging to:', coords);

      // Update in-memory feature collection
      const featureIdx = markerIndexById[currentMarkerId!];
      if (featureIdx != null && consolidatedMarkersData.features[featureIdx]) {
        (consolidatedMarkersData.features[featureIdx] as any).geometry.coordinates = [coords.lng, coords.lat];
        try { src.setData(consolidatedMarkersData as any); } catch (_) {}
      }

      // Update drag preview if snapping
      const stateAny = state as any;
      console.log('[MARKERS] onMove - isSnap:', stateAny.isSnap);
      if (stateAny.isSnap) {
        const routeId = stateAny.activeRouteKey ? stateAny.routes[stateAny.activeRouteKey].id : null;
        console.log('[MARKERS] onMove - updating drag preview for route:', routeId);
        if (routeId != null) {
          // update preview for current mouse position
          await (dispatch as any)('updateDragPreview', [coords.lng, coords.lat]);
        }
      }
      canvas.style.cursor = 'grabbing';
    }

    async function onUp(e: mapboxgl.MapMouseEvent) {
      map.off('mousemove', onMove);
      map.off('touchmove', onMove as any);
      document.removeEventListener('keydown', onKeyDown);

      const endClickTime = Date.now();
      const stateAny = state as any;

      // Try to finalize preview if snap is enabled and preview was started
      let previewApplied = false;
      if (stateAny.isSnap && stateAny.dragPreview.isDragging) {
        previewApplied = await (dispatch as any)('finalizeDragPreview');
      }

      // If preview wasn't applied (either not using snap or preview wasn't ready),
      // fall back to normal marker movement
      if (!previewApplied && currentMarkerIndex != null && lastDragLngLat) {
        const activeRoute = stateAny.activeRouteKey ? stateAny.routes[stateAny.activeRouteKey] : null;
        if (activeRoute) {
          const marker = activeRoute.markers[currentMarkerIndex];
          if (marker) {
            // Capture old position before updating for the undo command
            const oldPosition = [...marker.markerGeometry.coordinates];

            marker.markerGeometry.coordinates = [lastDragLngLat.lng as any, lastDragLngLat.lat as any];
            await (dispatch as any)('markerMoved', { marker, oldPosition });
          }
        }
      }

      canvas.style.cursor = 'move';

      // Reset drag state
      startClickTime = null;
      lastDragLngLat = null;
      currentMarkerId = null;
      currentMarkerIndex = null;
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        map.off('mousemove', onMove);
        map.off('touchmove', onMove as any);
        document.removeEventListener('keydown', onKeyDown);

        // Revert visual position to store state
        consolidatedMarkersData = (markerActions as any).buildConsolidatedMarkersData({ state } as any);
        const src = map.getSource(CONSOLIDATED_MARKER_SOURCE) as mapboxgl.GeoJSONSource;
        if (src && consolidatedMarkersData) {
          try { src.setData(consolidatedMarkersData as any); } catch (_) {}
        }

        // Cancel preview if active
        const stateAny = state as any;
        if (stateAny.dragPreview.isDragging) {
          (dispatch as any)('cancelDragPreview');
        }
        canvas.style.cursor = 'move';
      }
    }

    function enter(e: any) {
      console.log('[MARKERS] Mouse entered marker layer');
      canvas.style.cursor = 'move';
    }

    function leave(e: any) {
      console.log('[MARKERS] Mouse left marker layer');
      canvas.style.cursor = 'crosshair';
    }

    function mouseDown(e: mapboxgl.MapMouseEvent & { features?: any[] }) {
      console.log('[MARKERS] mouseDown triggered', e.features);
      e.preventDefault();

      // Synchronously check if editing is allowed (no modal)
      if (typeof window.canEditReference === 'function' && !window.canEditReference()) {
        console.log('[MARKERS] Cannot edit - user needs to confirm referenced route edit first');
        // Show the warning modal
        if (typeof window.checkReferenceEditWarning === 'function') {
          window.checkReferenceEditWarning().then(confirmed => {
            if (confirmed) {
              console.log('[MARKERS] User confirmed - they can now drag markers');
            }
          });
        }
        return;
      }

      startClickTime = Date.now();
      const feat = e.features && e.features[0];
      if (!feat) {
        console.log('[MARKERS] No feature found');
        return;
      }
      currentMarkerId = feat.properties?.markerId || null;
      currentMarkerIndex = feat.properties?.markerIndex ?? null;
      console.log('[MARKERS] Starting drag for marker:', currentMarkerId, 'index:', currentMarkerIndex);

      const stateAny = state as any;
      if (stateAny.isSnap && currentMarkerIndex != null) {
        // Use the activeRouteKey, not the route's internal ID
        const routeKey = stateAny.activeRouteKey;
        if (routeKey) {
          console.log('[MARKERS] Initializing drag preview with routeKey:', routeKey);
          (dispatch as any)('initializeDragPreview', {
            markerIndex: currentMarkerIndex,
            routeId: routeKey,  // Pass the route key, not the internal ID
            position: [feat.geometry.coordinates[0], feat.geometry.coordinates[1]]
          });
        }
      }

      map.on('mousemove', onMove);
      map.once('mouseup', onUp);
      document.addEventListener('keydown', onKeyDown);
    }

    function touchStart(e: any) {
      if (!e.points || e.points.length !== 1) return;
      e.preventDefault();
      map.on('touchmove', onMove as any);
      map.once('touchend', onUp as any);
    }

    // Store refs for cleanup
    functionRefs[CONSOLIDATED_MARKER_LAYER] = [onMove, onUp, enter, leave, mouseDown, touchStart];
    map.on('mouseenter', CONSOLIDATED_MARKER_LAYER, enter);
    map.on('mouseleave', CONSOLIDATED_MARKER_LAYER, leave);
    map.on('mousedown', CONSOLIDATED_MARKER_LAYER, mouseDown as any);
    map.on('touchstart', CONSOLIDATED_MARKER_LAYER, touchStart as any);
    console.log('[MARKERS] Listeners attached successfully');
  },

  /**
   * Remove consolidated marker layer and source
   */
  async removeConsolidatedMarkersFromMap({ }: PlanTrailActionContext) {
    const map = getMap();
    if (!map) return;
    safeRemoveLayer(CONSOLIDATED_MARKER_LAYER);
    if (map.getSource(CONSOLIDATED_MARKER_SOURCE)) {
      try { map.removeSource(CONSOLIDATED_MARKER_SOURCE); } catch (_) {}
    }
  },
  // Legacy function - removed. Use addAllRoutesToMap instead.

  // Legacy function - removed. Use addRouteMarkerToMap instead.

  async removeMarkerById({ state, commit, dispatch }: PlanTrailActionContext, { routeKey, markerIndex }: { routeKey: string, markerIndex: number }) {
    const route = state.routes[routeKey];
    if (!route || markerIndex < 0 || markerIndex >= route.markers.length) {
      console.error('Invalid route or marker index');
      return;
    }

    // Capture state before removal for the undo command
    const removedMarker = route.markers[markerIndex];
    let lsStartIndex: number;
    let lineStringsBefore: LineStringFeature[];
    let lineStringsBeforeCount: number;

    // Determine which linestrings are affected
    if (route.markers.length <= 2) {
      // Will clear all linestrings
      lsStartIndex = 0;
      lineStringsBefore = [...route.lineStrings];
      lineStringsBeforeCount = route.lineStrings.length;
    } else if (markerIndex === 0) {
      lsStartIndex = 0;
      lineStringsBefore = route.lineStrings.length > 0 ? [route.lineStrings[0]] : [];
      lineStringsBeforeCount = lineStringsBefore.length;
    } else if (markerIndex >= route.markers.length - 1) {
      lsStartIndex = route.lineStrings.length > 0 ? route.lineStrings.length - 1 : 0;
      lineStringsBefore = route.lineStrings.length > 0 ? [route.lineStrings[route.lineStrings.length - 1]] : [];
      lineStringsBeforeCount = lineStringsBefore.length;
    } else {
      // Middle marker — two adjacent linestrings
      lsStartIndex = markerIndex - 1;
      lineStringsBefore = route.lineStrings.slice(markerIndex - 1, markerIndex + 1);
      lineStringsBeforeCount = 2;
    }

    // Now perform the removal
    route.markers.splice(markerIndex, 1);

    // Handle linestrings based on the marker position
    if (route.markers.length === 0) {
      route.lineStrings = [];
    } else if (route.markers.length === 1) {
      route.lineStrings = [];
    } else if (markerIndex === 0) {
      if (route.lineStrings.length > 0) {
        route.lineStrings.splice(0, 1);
      }
    } else if (markerIndex >= route.markers.length) {
      if (route.lineStrings.length > 0) {
        route.lineStrings.splice(route.lineStrings.length - 1, 1);
      }
    } else {
      if (markerIndex - 1 < route.lineStrings.length && markerIndex < route.lineStrings.length) {
        const lineString1 = route.lineStrings[markerIndex - 1];
        const lineString2 = route.lineStrings[markerIndex];
        const mergedCoords = [
          ...lineString1.lineStringGeometry.coordinates.slice(0, -1),
          ...lineString2.lineStringGeometry.coordinates.slice(1)
        ];
        const mergedLineString = LineStringFeature.coordsOnlyFeature(mergedCoords);
        mergedLineString.lineStringProperties = { ...lineString1.lineStringProperties };
        mergedLineString.lineStringProperties.id = generateID();
        route.lineStrings.splice(markerIndex - 1, 2, mergedLineString);
      }
    }

    // Capture linestrings after removal — determine how many are at the affected position now
    let lineStringsAfter: LineStringFeature[];
    if (route.markers.length <= 1) {
      // 0 or 1 markers left — no linestrings
      lineStringsAfter = [];
    } else if (markerIndex === 0 || markerIndex >= route.markers.length) {
      // Removed first or last — one linestring was removed, nothing replaced it
      lineStringsAfter = [];
    } else {
      // Middle marker — two linestrings were replaced by one merged linestring
      lineStringsAfter = lsStartIndex < route.lineStrings.length
        ? [route.lineStrings[lsStartIndex]]
        : [];
    }

    // Push command
    state.undoManager.push(new RemoveMarkerCommand({
      routeKey,
      markerIndex,
      removedMarker,
      lineStringsBefore,
      lineStringsAfter,
      lineStringsStartIndex: lsStartIndex,
      lineStringsBeforeCount,
    }));

    route.updated_at = new Date();
    await dispatch('updateRouteAndMarkers');
  },

  async shouldRemoveMarker({ state, commit, dispatch }: PlanTrailActionContext, marker: MarkerFeature) {
    // This function is deprecated - use removeMarkerById instead
    console.warn('shouldRemoveMarker is deprecated, use removeMarkerById');
    return;
  },

  async removeAllControlPoints({ state, dispatch }: PlanTrailActionContext) {
    const routeKey = state.activeRouteKey;
    if (!routeKey) return;

    const route = state.routes[routeKey];
    if (!route || route.markers.length <= 2 || route.lineStrings.length === 0) return;

    const markersBefore = JSON.parse(JSON.stringify(route.markers));
    const lineStringsBefore = JSON.parse(JSON.stringify(route.lineStrings));

    const firstMarker = route.markers[0];
    const lastMarker = route.markers[route.markers.length - 1];

    // Merge all linestring coordinates into one, dropping duplicate junction points
    let mergedCoords: number[][] = [];
    for (const ls of route.lineStrings) {
      const coords = ls.lineStringGeometry.coordinates;
      if (mergedCoords.length === 0) {
        mergedCoords = [...coords];
      } else {
        mergedCoords = [...mergedCoords, ...coords.slice(1)];
      }
    }

    const mergedLineString = LineStringFeature.coordsOnlyFeature(mergedCoords);
    mergedLineString.lineStringProperties = {
      ...route.lineStrings[0].lineStringProperties,
      id: generateID(),
    };

    route.markers = [firstMarker, lastMarker];
    route.lineStrings = [mergedLineString];
    route.updated_at = new Date();

    state.undoManager.push(new RemoveAllControlPointsCommand({
      routeKey,
      markersBefore,
      lineStringsBefore,
      markersAfter: JSON.parse(JSON.stringify(route.markers)),
      lineStringsAfter: JSON.parse(JSON.stringify(route.lineStrings)),
    }));

    await dispatch('updateRouteAndMarkers');
  },

  // Legacy function - removed. Use removeRouteMarkerFromMap instead.

  // Legacy function - removed. Use createRouteMarkerLayer instead.

  // Legacy function - removed. Use addAllRoutesToMap instead.

  addListenersForMarker({ state, dispatch, getters }: PlanTrailActionContext, feature: MarkerFeature | { marker: MarkerFeature, layerId: string }) {
    const map = getMap();
    const canvas = map.getCanvasContainer();

    // Handle both old and new call signatures
    let markerFeature: MarkerFeature;
    let id: string;

    if ('marker' in feature && 'layerId' in feature) {
      // New signature with explicit layer ID
      markerFeature = feature.marker;
      id = feature.layerId;
    } else {
      // Old signature - feature is the marker
      markerFeature = feature as MarkerFeature;
      id = markerFeature.markerProperties.id;
    }

    // Get services
    const services = ServiceProvider.getInstance().getServices();
    const { mapService, eventBus } = services;

    const added = Date.now();

    async function onMove(e: mapboxgl.MapMouseEvent) {
      // Check if the source still exists before trying to update it
      const geoJsonSource = map.getSource(id) as mapboxgl.GeoJSONSource;
      if (!geoJsonSource) {
        // Source has been removed, clean up listeners
        map.off("mousemove", onMove);
        map.off("touchmove", onMove);
        return;
      }

      const coords = e.lngLat;
      canvas.style.cursor = "grabbing";
      markerFeature.markerGeometry.coordinates = [coords.lng, coords.lat];
      // @ts-ignore
      geoJsonSource.setData(markerFeature.toGeoJson());
      
      // Update drag preview
      if (state.isSnap) {
        await dispatch('updateDragPreview', [coords.lng, coords.lat]);
      }
    }

    async function onUp(e: mapboxgl.MapMouseEvent) {
      map.off("mousemove", onMove);
      map.off("touchmove", onMove);
      document.removeEventListener('keydown', onKeyDown);
      endClickTime = Date.now();
      if (startClickTime != null && endClickTime - startClickTime < 200) {
        startClickTime = null;
        await dispatch('shouldRemoveMarker', markerFeature);
      } else {
        // Try to apply preview if snap is enabled
        let previewApplied = false;
        if (state.isSnap && state.dragPreview.isDragging) {
          previewApplied = await dispatch('finalizeDragPreview');
        }

        // If preview wasn't applied, fall back to regular marker move
        if (!previewApplied) {
          await dispatch('markerMoved', markerFeature);
        }
      }
      canvas.style.cursor = "move";
    }

    function enterStyleMarker() {
      if (!map.getLayer(id)) return;
      map.setPaintProperty(id, "icon-halo-color", "#3887be");
      map.setPaintProperty(id, "icon-halo-width", 3);
      canvas.style.cursor = "move";
    }

    function leaveStyleMarker() {
      if (map.getLayer(id) != null) {
        map.setPaintProperty(id, "icon-halo-color", "#ffffff");
        map.setPaintProperty(id, "icon-halo-width", 2);
      }
      canvas.style.cursor = "crosshair";
    }

    function mouseDown(e: mapboxgl.MapMouseEvent) {
      // Prevent the default map drag behavior.
      startClickTime = Date.now();
      e.preventDefault();
      canvas.style.cursor = "grab";
      
      // Initialize drag preview if snap is enabled
      if (state.isSnap) {
        const routeId = getters.activeRouteId;
        const markerIndex = getters.activeRoute.markers.findIndex(
          (m: MarkerFeature) => m.markerProperties.id === markerFeature.markerProperties.id
        );
        
        if (routeId && markerIndex >= 0) {
          dispatch('initializeDragPreview', {
            markerIndex,
            routeId,
            position: [markerFeature.markerGeometry.coordinates[0], markerFeature.markerGeometry.coordinates[1]]
          });
        }
      }
      
      map.on("mousemove", onMove);
      map.once("mouseup", onUp);
      document.addEventListener('keydown', onKeyDown);
    }

    function touchStart(e: any) {
      if (e.points.length !== 1) return;
      // Prevent the default map drag behavior.
      e.preventDefault();

      map.on("touchmove", onMove);
      map.once("touchend", onUp);
    }
    
    // Handle ESC key to cancel drag
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Cancel drag and preview
        map.off("mousemove", onMove);
        map.off("touchmove", onMove);
        document.removeEventListener('keydown', onKeyDown);
        
        // Reset marker to original position
        const geoJsonSource = map.getSource(id) as mapboxgl.GeoJSONSource;
        if (geoJsonSource && markerFeature) {
          // Find original position from state
          const activeRoute = getters.activeRoute;
          const originalMarker = activeRoute.markers.find(
            (m: MarkerFeature) => m.markerProperties.id === markerFeature.markerProperties.id
          );
          if (originalMarker) {
            markerFeature.markerGeometry.coordinates = [...originalMarker.markerGeometry.coordinates];
            // @ts-ignore
            geoJsonSource.setData(markerFeature.toGeoJson());
          }
        }
        
        // Cancel preview if active
        if (state.dragPreview.isDragging) {
          dispatch('cancelDragPreview');
        }
        
        canvas.style.cursor = "move";
      }
    }

    if (functionRefs[id] != null) {
      functionRefs[id].forEach((ref) => {
        map.off("mouseenter", id, ref);
        map.off("mouseleave", id, ref);
        map.off("mousedown", id, ref);
        map.off("touchstart", id, ref);
      });
    }

    functionRefs[id] = [onMove, onUp, enterStyleMarker, leaveStyleMarker, mouseDown, touchStart];

    map.on("mouseenter", id, enterStyleMarker);
    map.on("mouseleave", id, leaveStyleMarker);
    map.on("mousedown", id, mouseDown);
    map.on("touchstart", id, touchStart);

    let startClickTime: number | null = null;
    let endClickTime: number | null = null;
  },

  async markerMoved({ state, commit, dispatch, getters }: PlanTrailActionContext, payload: MarkerFeature | { marker: MarkerFeature, oldPosition: number[] }) {
    // Support both old call signature (just marker) and new one (marker + oldPosition)
    const marker: MarkerFeature = 'marker' in payload ? payload.marker : payload;
    const oldPosition: number[] | null = 'oldPosition' in payload ? payload.oldPosition : null;

    const map = getMap();
    if (map.getSource('marker')) {
      map.removeLayer('marker');
      map.removeSource('marker');
    }

    // Update marker
    marker.markerProperties.isSnap = state.isSnap;
    await commit('updateMarker', marker);

    // Find marker index and capture old adjacent linestrings BEFORE they're replaced
    const markers = getters.activeRoute.markers;
    const index = markers.findIndex(m => m.markerProperties.id == marker.markerProperties.id);

    const activeRoute = getters.activeRoute;
    const oldLineStrings: { index: number; lineString: any }[] = [];
    if (oldPosition && activeRoute) {
      // Capture adjacent linestrings before replacement
      if (index > 0 && activeRoute.lineStrings[index - 1]) {
        oldLineStrings.push({ index: index - 1, lineString: JSON.parse(JSON.stringify(activeRoute.lineStrings[index - 1])) });
      }
      if (index < activeRoute.lineStrings.length && activeRoute.lineStrings[index]) {
        oldLineStrings.push({ index: index, lineString: JSON.parse(JSON.stringify(activeRoute.lineStrings[index])) });
      }
    }

    // Update linestrings (creates provisional lines that will be replaced by GraphHopper)
    await dispatch('updateLineStringsFromMarkerMove', index);

    // Create the MoveMarkerCommand with old position + old linestrings
    if (oldPosition && state.activeRouteKey) {
      const newLineStrings: { index: number; lineString: any }[] = [];
      if (index > 0 && activeRoute.lineStrings[index - 1]) {
        newLineStrings.push({ index: index - 1, lineString: JSON.parse(JSON.stringify(activeRoute.lineStrings[index - 1])) });
      }
      if (index < activeRoute.lineStrings.length && activeRoute.lineStrings[index]) {
        newLineStrings.push({ index: index, lineString: JSON.parse(JSON.stringify(activeRoute.lineStrings[index])) });
      }

      state.undoManager.push(new MoveMarkerCommand({
        routeKey: state.activeRouteKey,
        markerIndex: index,
        oldPosition,
        newPosition: [...marker.markerGeometry.coordinates],
        oldLineStrings,
        newLineStrings,
      }));
    }
  },

  // Route-specific marker actions
  async addRouteMarkerToMap({state, dispatch }: PlanTrailActionContext, data: {
    marker: MarkerFeature,
    routeId: string,
    markerIndex: number,
    isActive: boolean
  }) {
    const { marker, routeId, markerIndex, isActive } = data;
    const markerFeature = plainToClass(MarkerFeature, marker);
    const layerRegistry = getLayerRegistry();
    const route = state.routes[routeId];

    // Create unique ID for this route marker
    const id = `route-${routeId}-marker-${markerIndex}`;
    marker.markerProperties.id = id;

    // Remove existing marker if it exists
    await dispatch('removeRouteMarkerFromMap', { routeId, markerIndex });

    // Get source and add route/marker properties
    const source = markerFeature.toGeoJSONSourceRaw();
    // Add properties to the feature for identification
    if (source.data && source.data.type === 'Feature') {
      source.data.properties = {
        ...source.data.properties,
        routeId: routeId,
        markerIndex: markerIndex,
        markerId: markerFeature.markerProperties.id
      };
    }

    // Create layer
    const layer = await dispatch("createRouteMarkerLayer", {
      id,
      isActive,
      isFirst: markerIndex === 0,
      isLast: route ? markerIndex === route.markers.length - 1 : false
    });

    // Add layer and source together to avoid race condition
    layerRegistry.addLayer(layer, MapLayerCategory.Annotations, {
      z: isActive ? 13 : 11,
      source: { id, spec: source, replace: true }
    });

    if (isActive) {
      // Pass the actual layer ID to the listeners
      await dispatch('addListenersForRouteMarker', {
        marker: markerFeature,
        routeId,
        layerId: id,
        markerIndex
      });
    }
  },

  async removeRouteMarkerFromMap({ }: PlanTrailActionContext, data: { routeId: string, markerIndex: number }) {
    const { routeId, markerIndex } = data;
    const id = `route-${routeId}-marker-${markerIndex}`;
    safeRemoveLayer(id);
  },

  createRouteMarkerLayer({ }: PlanTrailActionContext, data: { id: string, isActive: boolean, isFirst: boolean, isLast: boolean }): mapboxgl.SymbolLayer {
    const { id, isActive, isFirst, isLast } = data;

    // Determine which icon to use
    const getIconImage = () => {
      if (isFirst) return 'hike-control-point-start';
      if (isLast) return 'hike-control-point-end';
      return 'hike-control-point-mid';
    };

    // Determine icon size based on state
    const getIconSize = () => {
      if (isActive) {
        return isFirst || isLast ? 0.25 : 0.25;
      }
      return isFirst || isLast ? 0.25 : 0.25;
    };

    return {
      id: id,
      type: "symbol",
      source: id,
      layout: {
        "icon-image": getIconImage(),
        "icon-size": getIconSize(),
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-anchor": "center",
      },
      paint: {
        "icon-opacity": isActive ? 1.0 : 0.6,
        "icon-halo-color": "#ffffff",
        "icon-halo-width": isActive ? 2 : 1,
        "icon-halo-blur": 0,
      },
    };
  },

  async addListenersForRouteMarker({ state, dispatch }: PlanTrailActionContext, data: {
    marker: MarkerFeature,
    routeId: string,
    layerId: string,
    markerIndex: number
  }) {
    // Only add listeners for active route markers
    const activeRoute = state.activeRouteKey ? state.routes[state.activeRouteKey] : null;
    if (activeRoute && activeRoute.id === data.routeId) {
      // Call addListenersForMarker with the layer ID
      await dispatch('addListenersForMarker', {
        marker: data.marker,
        layerId: data.layerId
      });
    }
  }
};
